<script lang="ts" setup>
import { computed, onBeforeMount, Ref, ref, watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useFloating, offset } from "@floating-ui/vue";
import { X, ArrowLeft, ChevronDown, Info } from "lucide-vue-next";
import { Auction, AuctionStatus, Scarcity } from "fungi-types";

// Router
import router from "~/router";
import Trans from "~/modules/translations";

// Utils
import { useI18n } from "vue-i18n";
import { BigNumber } from "ethers";
import { maticMinAmount } from "~/application/config";
import { getCurrencySymbol } from "~/utils/currency";
import { useTracking } from "~/composables/useGtm";
import { roundToDecimals } from "~/utils/math";

// Components
import Modal from "@/components/modals/ModalContainer.vue";
import useConvert from "~/composables/useConvert";
import CardDataFooterSkeleton from "../card/CardDataFooterSkeleton.vue";
import CardDataFooterPrice from "../card/CardDataFooterPrice.vue";
import ModalDeposit from "./ModalDeposit.vue";

// Stores
import { useStoreApp } from "~/stores/storeApp";
import { useStoreMarketplace } from "~/stores/storeMarketplace";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import usePOLPrice from "~/stores/POLprice";

// Payments
import { useStripeStore } from "~/composables/useStripe";
import Currency from "~/common/enums/Currency";

// Types
import { MarketType } from "fungi-types";
import { PaymentMethod } from "~/api/auction/types";
import ModalPackSwiper from "./ModalPackSwiper.vue";

import { useStorePack } from "~/stores/storePack";
import { OpenedPack } from "svc-auction/src/services/packs";
import { TRPCClientError } from "@trpc/client";

const showCurrencyDropdown = ref(false);
const toggleCurrencyDropdown = () => {
  showCurrencyDropdown.value = !showCurrencyDropdown.value;
};
const reference = ref(null);
const currencyDropdown = ref(null);
const { floatingStyles } = useFloating(reference, currencyDropdown, {
  middleware: [offset(10)],
});

onClickOutside(currencyDropdown, () => {
  showCurrencyDropdown.value = false;
});

const gtm = useTracking();

const { t } = useI18n();
const storePack = useStorePack();
const storeMarketplace = useStoreMarketplace();
const storeUser = useStoreUserV2();
const storeApp = useStoreApp();

const { weiToMatic, maticToWei } = useConvert;
const polPrice = usePOLPrice();

const isLogged = computed(() => storeUser.isConnected);
const marketType = computed(() => MarketType.PRIMARY);
const isPrimaryMarket = true;

// Currency Symbol
const currencySymbol = computed(() => {
  if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
    return getCurrencySymbol(currency.value as Currency);
  } else {
    return Currency.WPOL;
  }
});

// Insufficient Crypto Funds
const insufficientCryptoFunds = computed(() => {
  if (paymentMethod.value !== PaymentMethod.CRYPTO) return false;
  if (bidAmountWpol.value > storeUser.currentWMaticBalance) {
    return true;
  }
  return false;
});

const showModalDeposit = ref(false);
const closeModalDeposit = () => {
  showModalDeposit.value = false;
};
const addFund = () => {
  showModalDeposit.value = true;
};

// Handle Payment Method
const currency = ref<Currency | "wmtc">(storeUser.preferredCurrency ?? Currency.USD);
const paymentMethod = ref<PaymentMethod>(
  currency.value === "wmtc" ? PaymentMethod.CRYPTO : PaymentMethod.CREDIT_CARD
);
const updateCurrency = (value: Currency) => {
  showCurrencyDropdown.value = false;
  // storeUser.updatePreferredCurrency(value);
  currency.value = value;
  polPrice.refreshRates();
};

// Sync the min amount with the current item
const isBelowMinAmount = computed(() => {
  const resp = {
    isBelow: false,
    minAmount: 0,
  };

  if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
    resp.minAmount = roundToDecimals(
      polPrice.weiToCurrency(minAmountWei.value, currency?.value),
      2
    );
    resp.isBelow = bidAmountFiat.value < resp.minAmount;
  } else {
    resp.minAmount = weiToMatic(minAmountWei.value);
    resp.isBelow = bidAmountWpol.value < resp.minAmount;
  }
  return resp;
});

const errorCrypto = ref<null | string>(null);
const errorFiat = ref<null | string>(null);

// initAmounts
const minAmountWei = ref(BigNumber.from(0));
const bidAmountWpol = ref(0);
const bidAmountFiat = ref(0);
const hasChangedBidAmount = ref(false);
const fiatError = ref(false);

const defaultFiatCurrency = Currency.EUR;

const handlePaymentMethodChange = (method: PaymentMethod) => {
  if (method === PaymentMethod.CREDIT_CARD) updateCurrency(defaultFiatCurrency);
  paymentMethod.value = method;
};

const showVideo = ref(false);
const videoOpacity = ref(false);
const handleErrorPurchase = (error: unknown) => {
  storePack.buyInProgress = false;
  if (error instanceof TRPCClientError) {
    let errorData: any = error?.data;
    if (errorData?.code === 404) {
      handleErrorMsg({ message: t("pack.no-more-stock") });
    } else {
      handleErrorMsg({ message: t("_shared.error-try-again-later") });
    }
  } else {
    if (error?.message?.includes("does not have the amount needed")) {
      handleErrorMsg({ message: t("_payment.insufficient-funds") });
    } else {
      handleErrorMsg({ message: t("_shared.error-try-again-later") });
    }
  }
};
const handleSuccessfulPurchase = (pack: OpenedPack) => {
  storeUser.refreshBalance();
  storePack.packOpened = pack;
  showVideo.value = true;

  if (videoRef.value) {
    videoRef.value.muted = false;
    videoRef.value.volume = 1;
    videoRef.value.play();
  }

  videoRef.value?.play();
  setTimeout(() => {
    storeMarketplace.bidInProgress = false;
  }, 5000);
};

const buyPack = async () => {
  try {
    if (!isLogged.value) {
      handleCloseModal();
      login();
      return;
    } else if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
      buyWithStripe();
    } else {
      storePack.buyInProgress = true;
      const pack = await storePack.buyPackOnChain(
        storePack.selectedItem.pack_type,
        _weiAmount.value
      );

      if (pack.buyerId) handleSuccessfulPurchase(pack);
    }
  } catch (error) {
    handleErrorPurchase(error);
  }
};

const _weiAmount = computed(() => {
  const wpol = polPrice.currencyToPol(storePack?.selectedItem?.amount, Currency.EUR);
  const wei = maticToWei(wpol);
  return BigNumber.from(wei);
});

const initAmounts = () => {
  const minAmountWei = _weiAmount.value;

  bidAmountWpol.value = roundToDecimals(weiToMatic(minAmountWei), 2);
  if (currency.value === Currency.WMATIC) {
    bidAmountFiat.value = polPrice.weiToCurrency(minAmountWei, defaultFiatCurrency);
  } else {
    bidAmountFiat.value = roundToDecimals(polPrice.weiToCurrency(minAmountWei, currency.value), 2);
  }
};

onBeforeMount(() => {
  initAmounts();
  polPrice.refreshRates();
});

const handleErrorMsg = (error: any) => {
  if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
    errorFiat.value = error.message;
  } else {
    errorCrypto.value = error.message;
  }
};

async function login() {
  storeApp.setBidModalState(false);
  router.push(Trans.i18nRoute({ name: "SignIn" })).catch(console.error);
}

const checkoutRef = ref<HTMLElement>();
const stripe = useStripeStore();

const handleSuccessFulStripeBid = async () => {
  try {
    storeApp.setBidModalState(false);
    const pack = await stripe.buyPack();
    if (pack) {
      handleSuccessfulPurchase(pack);
    } else {
      storeMarketplace.bidInProgress = false;
      errorFiat.value = "Something went wrong";
    }
  } catch (error) {
    handleErrorPurchase(error);
  }
};

async function buyWithStripe() {
  if (!checkoutRef.value || isNaN(bidAmountFiat.value) || fiatError.value) {
    return;
  }

  if (BigNumber.from(Math.ceil(bidAmountFiat.value * 100)).gt(Number.MAX_SAFE_INTEGER - 1)) {
    throw new Error("Bid amount too high");
  }

  storePack.buyInProgress = true;

  await stripe.launchCheckoutSession({
    ref: checkoutRef as Ref<HTMLElement>,
    packType: storePack.selectedItem?.pack_type as Scarcity,
    amountInCents: Math.ceil(9.99 * 100),
    currency: currency.value as Currency,
    creditAmount: 0,
    successCallback: () => handleSuccessFulStripeBid(),
    errorCallback: () => {
      handleErrorPurchase(new Error("Something went wrong"));
      storePack.buyInProgress = false;
      polPrice.refreshRates();
    },
  });
}

function handleStripeModalBackButton() {
  stripe.cancelCheckoutSession();
  storePack.buyInProgress = false;
}

const emit = defineEmits<{
  (event: "onClose"): void;
}>();

const isInputFocused = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
function focusInput() {
  inputRef.value?.focus();
}

const handleInput = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  if (isNaN(Number(value))) return;
  hasChangedBidAmount.value = true;
  if (paymentMethod.value === PaymentMethod.CREDIT_CARD) {
    bidAmountFiat.value = Number(value);
  } else {
    bidAmountWpol.value = Number(value);
  }
};

const payments = computed(() => {
  const methods = [
    {
      method: PaymentMethod.CREDIT_CARD,
      label: t("_payment.credit-card"),
    },
    {
      method: PaymentMethod.CRYPTO,
      label: t("_shared.wallet"),
    },
  ];

  if (marketType.value === MarketType.SECONDARY) {
    return methods.filter((method) => method.method === PaymentMethod.CRYPTO);
  }
  return methods;
});

const handleCloseModal = () => {
  if (stripe.checkoutSessionInProgress) {
    handleStripeModalBackButton();
  } else if (showModalDeposit.value) {
    // Ignore closing modal if deposit modal is open
  } else {
    storePack.buyInProgress = false;
    emit("onClose");
  }
};

watch(
  () => currency.value,
  () => {
    initAmounts();
  }
);

const videoRef = ref<HTMLVideoElement | null>(null);
const showSwipeModal = ref(false);
// Function to check video time
const checkVideoTime = () => {
  if (!videoRef.value || showSwipeModal.value) return;

  const currentTime = videoRef.value.currentTime;
  const targetTime = 8; // The time in seconds when you want to trigger the function

  if (currentTime >= targetTime) {
    showSwipeModal.value = true;
  }
};

const packScarcity = computed(() => {
  return storePack.selectedItem?.pack_type;
});

const videoSrc = computed(() => {
  if (packScarcity.value === "gold") {
    return "https://storage.googleapis.com/fungiball-assets/pack-opening/gold-pack.mp4";
  } else if (packScarcity.value === "platinum") {
    return "https://storage.googleapis.com/fungiball-assets/pack-opening/platinum-pack.mp4";
  } else {
    return "https://storage.googleapis.com/fungiball-assets/pack-opening/silver-pack.mp4";
  }
});

const dropRate = computed(() => {
  const colorMap = {
    gold: {
      bg: "bg-yellow-500",
      text: "text-yellow-500",
    },
    platinum: {
      bg: "bg-purple-500",
      text: "text-purple-500",
    },
    silver: {
      bg: "bg-neutral-400",
      text: "text-neutral-400",
    },
    gray: {
      bg: "bg-neutral-400",
      text: "text-neutral-400",
    },
  };

  const chances: {
    tier: string;
    rate: number;
    bgColor: string;
    textColor: string;
    max: number;
    histogram?: string;
  }[] = [];

  const maxValue = Math.max(...Object.values(storePack.selectedItem.probabilities.tiers)) * 100;

  const scarcities = storePack.selectedItem?.probabilities?.scarcities;
  if (scarcities) {
    Object.entries(scarcities).forEach(([key, value]) => {
      let color = key as keyof typeof colorMap;
      chances.push({
        tier: `${key} card`,
        rate: Number((value * 100).toFixed(1)),
        bgColor: colorMap[color].bg,
        textColor: colorMap[color].text,
        max: maxValue,
      });
    });
  }

  Object.entries(storePack.selectedItem.probabilities.tiers).forEach(([key, value]) => {
    chances.push({
      tier: `Tier ${key}`,
      rate: Number((value * 100).toFixed(1)),
      bgColor: colorMap.gray.bg,
      textColor: colorMap.gray.text,
      max: maxValue,
    });
  });

  return chances.map((item) => ({
    ...item,
    histogram: `${Math.max(1, Math.round((item.rate / maxValue) * 100))}%`,
  }));
});

const auction = computed(() => {
  let auction: Auction = {
    cuid: storePack.selectedItem?.id || "",
    startedAmount: _weiAmount.value.toString(),
    endedAt: "",
    status: AuctionStatus.IN_PROGRESS,
    marketType: MarketType.PRIMARY,
    lastBidder: null,
    lastBidAt: null,
    countOrders: 0,
    lastOrderId: null,
    extraTime: 0,
    lastBidAmount: _weiAmount.value.toString(),
    type: "pack",
  };
  return auction;
});
</script>

<template>
  <Modal
    modalClasses="!p-0"
    size="4xl"
    :show="true"
    @on-close="handleCloseModal"
    :show-close-button="false"
  >
    <ModalDeposit v-if="showModalDeposit" :show="showModalDeposit" @onClose="closeModalDeposit" />
    <div
      v-show="stripe.checkoutSessionInProgress"
      class="bg-[#fff] p-4 text-slate-950 rounded-md w-full"
    >
      <div>
        <div class="font-semibold text-2xl text-center flex flex-row justify-between">
          <div>{{ t("_pack.title") }}</div>
          <button
            @click="handleStripeModalBackButton"
            class="p-2 rounded-full hover:bg-black-800 bg-black border-[1px] border-neutral-600"
          >
            <X class="h-4 w-4 fill-white text-white" />
          </button>
        </div>
        <OvTicker
          :cuid="storePack.selectedItem.id"
          :end-date="storeMarketplace.selectedItem.endedAt"
          :status="AuctionStatus.IN_PROGRESS"
          class="self-end text-sm [&_span]:text-neutral-500 stroke-neutral-500"
        />
      </div>
      <div class="stripe-checkout-container pt-2" ref="checkoutRef"></div>
      <div class="flex justify-center items-center pt-4">
        <Button @click="handleStripeModalBackButton" type="secondary">
          <div class="flex gap-2 text-sm items-center">
            <ArrowLeft class="h-4 w-4" />
            <span>
              {{ $t("modals.bid.back") }}
            </span>
          </div>
        </Button>
      </div>
    </div>
    <div v-show="!stripe.checkoutSessionInProgress" class="grid grid-cols-2 md:grid-cols-3">
      <div class="justify-center hidden md:flex">
        <PackSkillsItem :item="storePack.selectedItem" :showBuyButton="false" />
      </div>
      <div class="flex flex-col gap-4 col-span-2 md:px-10">
        <div class="font-semibold text-2xl text-center flex flex-row justify-between pt-5">
          <div>{{ t("_pack.title") }}</div>
          <button
            @click.stop="handleCloseModal"
            class="p-2 rounded-full hover:bg-slate-800 bg-black border-[1px] border-slate-600"
          >
            <X class="h-4 w-4" />
          </button>
        </div>

        <div class="flex flex-row gap-4 md:hidden">
          <img class="h-32 object-contain" :src="storePack.selectedItem.image" alt="" />

          <div
            class="bg-neutral-800 items-center justify-center rounded-md w-full gap-2 flex flex-col text-xs"
          >
            <CardDataFooterSkeleton>
              <div class="flex flex-col -gap-2">
                <div class="font-semibold tracking-wider">
                  {{ storePack.selectedItem.pack_type.toUpperCase() }} PACK
                </div>
                <span class="text-neutral-400 text-xs"> 4 cards - one per skill </span>
              </div>

              <CardDataFooterPrice :auction="auction" :stock="storePack.selectedItem?.stock" />
            </CardDataFooterSkeleton>
          </div>
        </div>

        <div class="rounded-md p-4 flex flex-col bg-neutral-800/60">
          <div class="font-semibold">
            {{
              t("_pack.description.title", {
                gender: t(`genders.${storePack.selectedItem?.gender}`),
                scarcity: t(`scarcities.${storePack.selectedItem?.pack_type}`),
              })
            }}
          </div>

          <div class="text-xs text-neutral-400 mb-2">
            • {{ t("_pack.description.skills") }} <br />
            •
            {{
              t(
                `_pack.description.${storePack.selectedItem?.gender === "Women" ? "female" : "male"}`
              )
            }}
          </div>

          <div class="font-semibold">Chances</div>
          <div class="flex flex-col gap-1 text-xs max-w-full mt-1">
            <div v-for="(rate, index) in dropRate" :key="rate.tier">
              <div class="text-xs justify-between flex" :class="`${rate.textColor}`">
                <div class="first-letter:capitalize">{{ rate.tier }}</div>
                <div class="flex items-center gap-3">
                  <div class="w-10 h-1 bg-neutral-800">
                    <div
                      class="h-full rounded-lg"
                      :class="`${rate.bgColor}`"
                      :style="{
                        width: rate.histogram,
                      }"
                    ></div>
                  </div>
                  <div class="w-8 text-right">{{ rate.rate }}%</div>
                </div>
              </div>
              <div
                v-if="index + 1 !== dropRate.length"
                class="w-full h-[1px] bg-neutral-700 mt-1"
              ></div>
            </div>
          </div>
        </div>

        <div class="h-[1px] w-full bg-neutral-800"></div>
        <div class="grid 2 w-full gap-4" :class="`grid-cols-${payments.length}`">
          <div
            v-for="payment in payments"
            :key="payment.method"
            @click="handlePaymentMethodChange(payment.method)"
            class="border transition-colors p-3 text-sm text-neutral-200 border-neutral-700 rounded-md flex flex-col gap-1 cursor-pointer"
            :class="[
              isPrimaryMarket && 'hover:border-blue-700',
              paymentMethod === payment.method &&
                isPrimaryMarket &&
                '!border-blue-700 !bg-blue-950/70 ',
            ]"
          >
            <icon-fg-credit-card
              v-if="payment.method === PaymentMethod.CREDIT_CARD"
              class="w-6 h-6 fill-white"
            />
            <icon-fg-w-pol-square v-else class="w-6 h-6 fill-white" />

            <div class="flex text-xs flex-row flex-wrap md:text-sm w-full">
              <div class="pr-2">
                {{ payment.label }}
              </div>

              <div
                class="flex flex-nowrap flex-row text-yellow-500 gap-0.5"
                v-if="payment.method === PaymentMethod.CRYPTO && isLogged"
              >
                <div>{{ storeUser.wpolRemaining.toFixed(2) }}</div>
                <div>WPOL</div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="insufficientCryptoFunds && isLogged"
          class="bg-yellow-400/20 rounded-md flex w-full justify-between p-2 items-center font-medium"
        >
          <div class="text-xs text-yellow-400 flex flex-row gap-2 items-center">
            <Info class="h-4 w-4" /> {{ t("_payment.insufficient-funds") }}
          </div>
          <Button @click="addFund" size="xs" type="primary-white"
            ><span class="font-semibold">{{ t("_payment.add-funds") }}</span></Button
          >
        </div>
        <div class="flex flex-col gap-4">
          <div
            @click.stop="focusInput"
            class="border border-transparent transition-colors px-3 py-4 gap-1 items-center text-neutral-200 bg-neutral-800 cursor-text rounded-md flex group hover:cursor-not-allowed"
            :class="[
              isInputFocused && '!border-blue-700 ',
              isBelowMinAmount.isBelow && '!border-red-800',
            ]"
          >
            <div class="flex flex-row items-center gap-2">
              <icon-fg-credit-card
                v-if="paymentMethod === PaymentMethod.CREDIT_CARD"
                class="w-5 h-5 fill-white"
              />
              <icon-fg-w-pol-square v-else class="w-5 h-5 fill-white" />
              <div class="text-sm">{{ t("_payment.amount") }}</div>
            </div>
            <input
              ref="inputRef"
              @focus="isInputFocused = true"
              @blur="isInputFocused = false"
              @input="handleInput"
              min="0"
              class="text-right font-semibold group-hover:cursor-not-allowed"
              :value="paymentMethod === PaymentMethod.CREDIT_CARD ? bidAmountFiat : bidAmountWpol"
              data-v-c3ac74de=""
              type="number"
              :disabled="true"
            />

            <div
              ref="currencyDropdown"
              :style="[floatingStyles, { visibility: showCurrencyDropdown ? 'visible' : 'hidden' }]"
              class="bg-white text-neutral-800 p-1 rounded-md z-50 flex flex-col gap-1"
            >
              <div
                v-for="item in [Currency.EUR, Currency.USD]"
                :key="item"
                class="hover:bg-blue-700 hover:text-white rounded-md p-2 cursor-pointer"
                :class="[item === currency ? 'bg-blue-700 text-white' : '']"
                @click="updateCurrency(item)"
              >
                {{ item.toUpperCase() }}
              </div>
            </div>

            <div
              @click.stop="toggleCurrencyDropdown"
              ref="reference"
              v-if="paymentMethod === PaymentMethod.CREDIT_CARD"
              class="font-medium border-l border-neutral-700 pl-2 ml-1 flex flex-row items-center gap-1 cursor-pointer group"
            >
              {{ currencySymbol }}
              <ChevronDown class="w-4 h-4 text-neutral-500 group-hover:text-white" />
            </div>
            <div v-else class="font-medium ml-1">
              <icon-fg-w-pol class="w-4 h-4" />
            </div>
          </div>

          <div class="h-[1px] w-full bg-neutral-800"></div>
          <Button
            :loading="storePack.buyInProgress"
            @click="buyPack"
            type="primary-blue"
            class="w-full"
          >
            <template v-if="isLogged">
              {{ t("_payment.confirm-buy") }}
            </template>
            <template v-else>
              {{ t("nav.login") }}
            </template>
          </Button>

          <div
            v-if="errorCrypto && paymentMethod === PaymentMethod.CRYPTO"
            class="border-red-600/2 bg-red-950 text-red-400 text-sm p-2 rounded-md text-center"
          >
            {{ errorCrypto }}
          </div>
          <div
            v-else-if="errorFiat && paymentMethod === PaymentMethod.CREDIT_CARD"
            class="border-red-600/2 bg-red-950 text-red-400 text-sm p-2 rounded-md text-center"
          >
            {{ errorFiat }}
          </div>
        </div>
      </div>
    </div>
  </Modal>

  <Transition name="fade">
    <ModalPackSwiper @close="handleCloseModal" v-if="showSwipeModal" :scarcity="packScarcity" />
  </Transition>
  <video
    :muted="false"
    preload="auto"
    playsinline
    class="fixed z-[999999999] flex inset-0 object-cover min-h-full min-w-full pointer-events-none transition-all duration-500 ease-in-out"
    :class="videoOpacity ? 'opacity-100' : 'opacity-0'"
    @play="videoOpacity = true"
    @timeupdate="checkVideoTime"
    @ended="storePack.buyInProgress = false"
    ref="videoRef"
  >
    <source :src="videoSrc" />
  </video>
</template>

<style scoped lang="scss">
input {
  @apply border-transparent bg-transparent outline-none w-full;
  box-shadow: none;
  border-radius: 0;
  &::placeholder {
    @apply text-neutral-500;
  }
}

/* Enter transitions */
.fade-enter-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}

/* Leave transitions */
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-leave-from {
  opacity: 1;
}
.fade-leave-to {
  opacity: 0;
}
</style>
